import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Users, Key, ClipboardList, Trash2, Copy, ChevronLeft, ChevronRight } from 'lucide-react';
import { Card } from '@/components/Card';
import { useToast } from '@theonefamily/ui';
import { get, post, patch, del } from '@/lib/api';

const TABS = [
  { key: 'team', label: 'Team', icon: Users },
  { key: 'api-keys', label: 'API Keys', icon: Key },
  { key: 'audit', label: 'Audit Log', icon: ClipboardList },
] as const;
type TabKey = typeof TABS[number]['key'];

// ─── Team ─────────────────────────────────────────────────────

interface TeamMember { id: string; name: string; email: string; role: string; }

function TeamTab() {
  const { success, error: showError } = useToast();
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('viewer');
  const [inviting, setInviting] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    get<{ data: TeamMember[] }>('admin/team')
      .then(r => setMembers((r as any).data ?? r ?? []))
      .catch(() => showError('Failed to load team'))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const invite = async () => {
    if (!inviteEmail.trim()) return;
    setInviting(true);
    try {
      await post('admin/team/invite', { email: inviteEmail.trim(), role: inviteRole });
      success(`Invitation sent to ${inviteEmail.trim()}`);
      setInviteEmail('');
      load();
    } catch { showError('Failed to send invitation'); }
    finally { setInviting(false); }
  };

  const updateRole = async (userId: string, role: string) => {
    try {
      await patch(`admin/team/${userId}/role`, { role });
      success('Role updated');
      load();
    } catch { showError('Failed to update role'); }
  };

  const remove = async (userId: string) => {
    try {
      await del(`admin/team/${userId}`);
      success('Member removed');
      load();
    } catch { showError('Failed to remove member'); }
  };

  if (loading) return <div className="text-sm text-muted-foreground py-8 text-center">Loading team...</div>;

  return (
    <div className="space-y-5">
      <Card>
        <h3 className="text-sm font-medium text-foreground mb-3">Invite Member</h3>
        <div className="flex gap-3 flex-wrap">
          <input
            value={inviteEmail}
            onChange={e => setInviteEmail(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') invite(); }}
            placeholder="Email address"
            className="flex-1 min-w-[200px] px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-amber-500"
          />
          <select
            value={inviteRole}
            onChange={e => setInviteRole(e.target.value)}
            className="px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground focus:outline-none focus:border-amber-500"
          >
            <option value="viewer">Viewer</option>
            <option value="editor">Editor</option>
            <option value="admin">Admin</option>
          </select>
          <button
            onClick={invite}
            disabled={inviting || !inviteEmail.trim()}
            className="px-4 py-2 rounded-lg bg-primary hover:bg-primary/90 text-primary-foreground font-semibold text-sm transition-colors disabled:opacity-50"
          >
            {inviting ? 'Inviting...' : 'Invite'}
          </button>
        </div>
      </Card>
      <Card className="overflow-hidden p-0">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left px-4 py-3 text-muted-foreground font-medium">Name</th>
              <th className="text-left px-4 py-3 text-muted-foreground font-medium">Email</th>
              <th className="text-left px-4 py-3 text-muted-foreground font-medium">Role</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {members.length === 0 ? (
              <tr><td colSpan={4} className="px-4 py-8 text-center text-muted-foreground">No team members found</td></tr>
            ) : members.map(m => (
              <tr key={m.id} className="border-b border-border/50 hover:bg-muted/20">
                <td className="px-4 py-3 text-foreground">{m.name}</td>
                <td className="px-4 py-3 text-muted-foreground">{m.email}</td>
                <td className="px-4 py-3">
                  <select
                    value={m.role}
                    onChange={e => updateRole(m.id, e.target.value)}
                    className="px-2 py-1 rounded border border-border bg-background text-xs text-foreground focus:outline-none focus:border-amber-500"
                  >
                    <option value="viewer">Viewer</option>
                    <option value="editor">Editor</option>
                    <option value="admin">Admin</option>
                  </select>
                </td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => remove(m.id)} className="p-1.5 text-muted-foreground hover:text-destructive transition-colors rounded">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

// ─── API Keys ─────────────────────────────────────────────────

interface ApiKey { id: string; name: string; key_prefix: string; last_used_at?: string; created_at: string; }

function ApiKeysTab() {
  const { success, error: showError } = useToast();
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [revealedKey, setRevealedKey] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    get<{ data: ApiKey[] }>('admin/api-keys')
      .then(r => setKeys((r as any).data ?? []))
      .catch(() => showError('Failed to load API keys'))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const res = await post<ApiKey & { key: string }>('admin/api-keys', { name: newName.trim() });
      setRevealedKey((res as any).key ?? null);
      setNewName('');
      load();
    } catch { showError('Failed to create API key'); }
    finally { setCreating(false); }
  };

  const revoke = async (id: string) => {
    try {
      await del(`admin/api-keys/${id}`);
      success('API key revoked');
      load();
    } catch { showError('Failed to revoke key'); }
  };

  return (
    <div className="space-y-5">
      {revealedKey && (
        <Card className="border-amber-500/30 bg-amber-500/5">
          <p className="text-sm font-medium text-amber-500 mb-2">API Key Created — copy it now, it won't be shown again</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 px-3 py-2 rounded-lg bg-background text-xs font-mono text-foreground break-all">{revealedKey}</code>
            <button
              onClick={() => { navigator.clipboard.writeText(revealedKey); success('Copied!'); }}
              className="p-2 text-muted-foreground hover:text-amber-500 transition-colors"
            >
              <Copy className="w-4 h-4" />
            </button>
          </div>
          <button onClick={() => setRevealedKey(null)} className="text-xs text-muted-foreground hover:text-foreground mt-2">Dismiss</button>
        </Card>
      )}
      <Card>
        <h3 className="text-sm font-medium text-foreground mb-3">Create API Key</h3>
        <div className="flex gap-3">
          <input
            value={newName}
            onChange={e => setNewName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') create(); }}
            placeholder="Key name"
            className="flex-1 px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-amber-500"
          />
          <button
            onClick={create}
            disabled={creating || !newName.trim()}
            className="px-4 py-2 rounded-lg bg-primary hover:bg-primary/90 text-primary-foreground font-semibold text-sm transition-colors disabled:opacity-50"
          >
            {creating ? 'Creating...' : 'Create'}
          </button>
        </div>
      </Card>
      <Card className="overflow-hidden p-0">
        {loading ? (
          <div className="text-sm text-muted-foreground py-8 text-center">Loading...</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left px-4 py-3 text-muted-foreground font-medium">Name</th>
                <th className="text-left px-4 py-3 text-muted-foreground font-medium">Prefix</th>
                <th className="text-left px-4 py-3 text-muted-foreground font-medium">Last Used</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {keys.length === 0 ? (
                <tr><td colSpan={4} className="px-4 py-8 text-center text-muted-foreground">No API keys</td></tr>
              ) : keys.map(k => (
                <tr key={k.id} className="border-b border-border/50 hover:bg-muted/20">
                  <td className="px-4 py-3 text-foreground">{k.name}</td>
                  <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{k.key_prefix}...</td>
                  <td className="px-4 py-3 text-muted-foreground">{k.last_used_at ? new Date(k.last_used_at).toLocaleDateString() : 'Never'}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => revoke(k.id)} className="p-1.5 text-muted-foreground hover:text-destructive transition-colors rounded">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}

// ─── Audit Log ────────────────────────────────────────────────

interface AuditEvent { id: string; actor_email?: string; action: string; action_category?: string; entity_type?: string; entity_name?: string; created_at: string; }

function AuditTab() {
  const { error: showError } = useToast();
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [filterCategory, setFilterCategory] = useState('');
  const [filterEmail, setFilterEmail] = useState('');
  const limit = 50;

  const load = useCallback((off: number, cat: string, em: string) => {
    setLoading(true);
    const params = new URLSearchParams({ limit: String(limit), offset: String(off) });
    if (cat) params.set('category', cat);
    if (em) params.set('actor_email', em);
    get<{ data: AuditEvent[]; pagination: { total: number } }>(`admin/audit-log?${params}`)
      .then(r => { setEvents((r as any).data ?? []); setTotal((r as any).pagination?.total ?? 0); })
      .catch(() => showError('Failed to load audit log'))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(0, '', ''); }, [load]);

  const applyFilters = () => { setOffset(0); load(0, filterCategory, filterEmail); };
  const prev = () => { const o = Math.max(0, offset - limit); setOffset(o); load(o, filterCategory, filterEmail); };
  const next = () => { const o = offset + limit; setOffset(o); load(o, filterCategory, filterEmail); };

  return (
    <div className="space-y-5">
      <Card>
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label className="block text-xs text-muted-foreground mb-1">Category</label>
            <select
              value={filterCategory}
              onChange={e => setFilterCategory(e.target.value)}
              className="px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground focus:outline-none focus:border-amber-500"
            >
              <option value="">All categories</option>
              <option value="auth">Auth</option>
              <option value="access">Access</option>
              <option value="config">Config</option>
              <option value="data_create">Data Create</option>
              <option value="data_update">Data Update</option>
              <option value="data_delete">Data Delete</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-muted-foreground mb-1">Actor email</label>
            <input
              value={filterEmail}
              onChange={e => setFilterEmail(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') applyFilters(); }}
              placeholder="Filter by email..."
              className="px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-amber-500"
            />
          </div>
          <button
            onClick={applyFilters}
            className="px-4 py-2 rounded-lg bg-muted hover:bg-muted/80 text-foreground text-sm transition-colors"
          >
            Filter
          </button>
        </div>
      </Card>
      <Card className="overflow-hidden p-0">
        {loading ? (
          <div className="text-sm text-muted-foreground py-8 text-center">Loading...</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left px-4 py-3 text-muted-foreground font-medium">Time</th>
                <th className="text-left px-4 py-3 text-muted-foreground font-medium">Actor</th>
                <th className="text-left px-4 py-3 text-muted-foreground font-medium">Action</th>
                <th className="text-left px-4 py-3 text-muted-foreground font-medium">Entity</th>
              </tr>
            </thead>
            <tbody>
              {events.length === 0 ? (
                <tr><td colSpan={4} className="px-4 py-8 text-center text-muted-foreground">No audit events found</td></tr>
              ) : events.map(e => (
                <tr key={e.id} className="border-b border-border/50 hover:bg-muted/20">
                  <td className="px-4 py-3 text-muted-foreground text-xs whitespace-nowrap">{new Date(e.created_at).toLocaleString()}</td>
                  <td className="px-4 py-3 text-xs text-foreground">{e.actor_email ?? '—'}</td>
                  <td className="px-4 py-3 font-mono text-xs text-amber-500">{e.action}</td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">{e.entity_name ?? e.entity_type ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
      {total > limit && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>{offset + 1}–{Math.min(offset + limit, total)} of {total}</span>
          <div className="flex gap-1">
            <button onClick={prev} disabled={offset === 0} className="p-1.5 rounded hover:bg-muted disabled:opacity-30"><ChevronLeft className="w-4 h-4" /></button>
            <button onClick={next} disabled={offset + limit >= total} className="p-1.5 rounded hover:bg-muted disabled:opacity-30"><ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────

export function AccessAdminPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabKey>('team');

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/admin')} className="p-1.5 rounded-lg hover:bg-muted transition-colors text-muted-foreground">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div>
          <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
            <Users className="w-5 h-5 text-amber-500" /> Team & Access
          </h2>
          <p className="text-xs text-muted-foreground">Manage team members, API keys, and review audit history</p>
        </div>
      </div>

      <div className="flex gap-1 border-b border-border">
        {TABS.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
              activeTab === tab.key
                ? 'border-amber-500 text-amber-500'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'team' && <TeamTab />}
      {activeTab === 'api-keys' && <ApiKeysTab />}
      {activeTab === 'audit' && <AuditTab />}
    </div>
  );
}
